import { useCallback, useEffect, useRef, useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { readFormDraft, removeFormDraft, writeFormDraft, type FormDraft } from "@/lib/formDraft";

export const DRAFT_DEBOUNCE_MS = 1500;
const FAILURES_BEFORE_WARNING = 2;

export type DraftSaveStatus = "idle" | "saving" | "saved" | "failed";

export interface LoadedFormDraft<T> {
  draft: FormDraft<T>;
  values: T;
}

export function mergeDraftValues<T extends FieldValues>(base: T, saved: Partial<T>): T {
  const values = { ...base };
  for (const k of Object.keys(base) as (keyof T)[]) {
    const v = saved[k];
    if (v !== undefined && typeof v === typeof base[k]) values[k] = v as T[keyof T];
  }
  return values;
}

export function loadFormDraft<T extends FieldValues>(key: string, empty: T): LoadedFormDraft<T> | null {
  const draft = readFormDraft<T>(key);
  if (!draft) return null;
  return { draft, values: mergeDraftValues(empty, draft.values) };
}

function isFilled(v: unknown): boolean {
  return typeof v === "string" ? v.trim() !== "" : v != null;
}

export function useFormDraftAutosave<T extends FieldValues>({
  form,
  storageKey,
  enabled,
  restored,
  step,
  maxValidated,
  files,
  sensitive = [],
}: {
  form: UseFormReturn<T>;
  storageKey: string;
  enabled: boolean;
  restored: FormDraft<T> | null;
  step: number;
  maxValidated: number;
  files: Record<string, string | undefined>;
  sensitive?: readonly string[];
}) {
  const [status, setStatus] = useState<DraftSaveStatus>("idle");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const timer = useRef<number | null>(null);
  const hasDraft = useRef(restored != null);
  const lastEditAt = useRef(restored?.lastEditAt ?? 0);
  const failures = useRef(0);
  const latest = useRef({ enabled, step, maxValidated, files, sensitive });

  useEffect(() => {
    latest.current = { enabled, step, maxValidated, files, sensitive };
  });

  const cancel = useCallback(() => {
    if (timer.current == null) return;
    window.clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const write = useCallback(() => {
    cancel();
    const cur = latest.current;
    if (!cur.enabled || !hasDraft.current) return;
    const all = form.getValues() as Record<string, unknown>;
    const fileNames: Record<string, string> = {};
    for (const [k, v] of Object.entries(cur.files)) {
      if (v) fileNames[k] = v;
    }
    const ok = writeFormDraft<T>(storageKey, {
      lastEditAt: lastEditAt.current,
      step: cur.step,
      maxValidated: cur.maxValidated,
      sensitive: cur.sensitive.some((f) => isFilled(all[f])),
      files: fileNames,
      values: all as Partial<T>,
    });
    if (ok) {
      failures.current = 0;
      setStatus("saved");
      setSavedAt(Date.now());
      return;
    }
    failures.current += 1;
    if (failures.current >= FAILURES_BEFORE_WARNING) {
      setStatus("failed");
      return;
    }
    timer.current = window.setTimeout(write, DRAFT_DEBOUNCE_MS);
  }, [cancel, form, storageKey]);

  const schedule = useCallback(() => {
    cancel();
    setStatus((s) => (s === "failed" ? s : "saving"));
    timer.current = window.setTimeout(write, DRAFT_DEBOUNCE_MS);
  }, [cancel, write]);

  const markEdited = useCallback(() => {
    if (!latest.current.enabled) return;
    hasDraft.current = true;
    lastEditAt.current = Date.now();
    schedule();
  }, [schedule]);

  const flush = useCallback(() => {
    if (timer.current != null) write();
  }, [write]);

  const discard = useCallback(() => {
    cancel();
    removeFormDraft(storageKey);
    hasDraft.current = false;
    lastEditAt.current = 0;
    failures.current = 0;
    setStatus("idle");
    setSavedAt(null);
  }, [cancel, storageKey]);

  useEffect(() => {
    const sub = form.watch((_, info) => {
      if (info.name) markEdited();
    });
    return () => sub.unsubscribe();
  }, [form, markEdited]);

  const position = useRef({ step, maxValidated });
  useEffect(() => {
    const prev = position.current;
    if (prev.step === step && prev.maxValidated === maxValidated) return;
    position.current = { step, maxValidated };
    write();
  }, [step, maxValidated, write]);

  const filesKey = JSON.stringify(files);
  const prevFiles = useRef(files);
  useEffect(() => {
    const prev = prevFiles.current;
    prevFiles.current = files;
    const added = Object.entries(files).some(([k, v]) => v && v !== prev[k]);
    const removed = Object.entries(prev).some(([k, v]) => v && !files[k]);
    if (added) markEdited();
    else if (removed && hasDraft.current) schedule();
  }, [filesKey]);

  useEffect(() => {
    if (!enabled) cancel();
  }, [enabled, cancel]);

  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onHide);
      flush();
    };
  }, [flush]);

  return { status, savedAt, flush, discard };
}
